<template>
  <form @submit.prevent="save">
    <div :class="dialog ? 'p-15' : 'py-2'">
      <edit-field ref="PASSWORD"
                  :editFieldInfo="passwordFieldInfo"
                  :required="true"
                  :value="password"
                  :password="true"
                  @error="setPasswordError"
                  @updated="setPassword">
      </edit-field>

      <edit-field ref="REPEAT_PASSWORD"
                  :editFieldInfo="repeatPasswordFieldInfo"
                  :required="true"
                  :value="repeatPassword"
                  :password="true"
                  @error="setRepeatPasswordError"
                  @updated="setRepeatPassword">
      </edit-field>
    </div>

    <div  :disabled="!updatedAndValid"></div>
    <div v-if="!hideButton" class="d-flex flex-row-reverse">
      <loader-button :disabled="!updatedAndValid"
                     :label="saveLabel"
                     class="ms-2"
                     type="submit"
                     @callback="save">
      </loader-button>
    </div>
  </form>
</template>

<script lang="ts">
import { ConnectionParam } from '@/generated/classes/Session';
import { storeSession, storeUser } from '@/generated/stores';
import LoaderButton from '@/framework/base/components/LoaderButton.vue';
import { FwkButtons, FwkControls, RessourceFwkButton, RessourceFwkControl, } from '@/ressources/globalRessources';
import EditField from '@/framework/base/components/EditField.vue';
import { EditFieldType, FieldError, FieldInfo } from '@/framework/base/components/FieldInfo';
import Validator, { Rule, rules, RULES_FRENCH_LABEL } from '@/generated/validators/ValidationRules';
import User from '@/generated/classes/User';
import { defineComponent } from "vue";

export default defineComponent({
  name: 'passwordComponent',
  components: {
    EditField,
    LoaderButton
  },
  props: {
    extended: {
      type: Boolean
    },
    dialog: {
      type: Boolean
    },
    reset: {
      type: Boolean
    },
    hideButton: {
      type: Boolean
    }
  },
  data() {
    return {
      password: '',
      passwordError: false,
      repeatPassword: '',
      repeatPasswordError: false,
      saveLabel: RessourceFwkButton(FwkButtons.ENREGISTRER).label
    };
  },
  computed: {
    passwordFieldInfo(): FieldInfo {
      const ressource = RessourceFwkControl(FwkControls.PASSWORD);
      const type = EditFieldType.STRING;
      const precision = 2;
      const format = null;
      const mask = null;
      const focus = this.passwordFocusValidator;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live);
    },
    repeatPasswordFieldInfo(): FieldInfo {
      const ressource = RessourceFwkControl(FwkControls.REPEAT_PASSWORD);
      const type = EditFieldType.STRING;
      const precision = 2;
      const format = null;
      const mask = null;
      const focus = this.repeatPasswordFocusValidator;
      const live = null;
      return new FieldInfo(ressource, type, precision, mask, format, focus, live);
    },
    user(): User {
      const connectedUser = storeSession.connectedUser;
      if (connectedUser) {
        return storeUser.getUser(connectedUser.id);
      } else {
        return new User();
      }
    },
    updatedAndValid(): boolean {
      return this.passwordCorrect;
    },
    passwordCorrect(): boolean {
      const res = this.repeatPassword != '' &&
        this.password != '' &&
        !this.passwordError &&
        !this.repeatPasswordError &&
        this.isSame(this.repeatPassword, this.password);
      this.$emit('valid', res);
      return res;
    }
  },
  methods: {
    passwordFocusValidator(value: string): Validator | null {
      return new Validator(rules.strongPassword, RULES_FRENCH_LABEL.text.strongPassword.error);
    },
    repeatPasswordFocusValidator(value: string): Validator | null {
      const rule = new Rule();
      rule.function = () => this.isSame(this.repeatPassword, this.password);
      rule.useMask = false;
      return new Validator(rule, RULES_FRENCH_LABEL.text.identique.error);
    },
    isSame(value1: string, value2: string) {
      return value1 === value2;
    },
    setPassword(value: string) {
      this.password = value;
    },
    setPasswordError(value: FieldError) {
      this.passwordError = value.error;
    },
    setRepeatPassword(value: string) {
      this.repeatPassword = value;
    },
    setRepeatPasswordError(value: FieldError) {
      this.repeatPasswordError = value.error;
    },
    resetFields(): void {
      this.repeatPassword = '';
      this.password = '';
    },
    save(): void {
      const param: ConnectionParam = new ConnectionParam();
      param.password = this.password;
      storeSession.setPassword(param).then((res) => {
        if (res) {
          this.user.hasPassword = true;
          this.user.connectionMode = 2;
        }
        this.resetFields();
      });
    },
    closeDialog(): void {
      this.$emit('closeDialog');
    }
  }
})

</script>

<style>
  .password div.v-messages {
    display: block !important;
  }
</style>
